import { supabase } from '@/integrations/supabase/client';

export interface UserTag {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  color: string;
  category: string;
  created_at: string;
  updated_at: string;
}

export interface SystemTag {
  id: string;
  name: string;
  description?: string;
  color: string;
  category: string;
  is_active: boolean;
  created_at: string;
}

export interface TaggedData {
  id: string;
  user_id: string;
  data_type: 'metric' | 'material' | 'carbon' | 'esg';
  data_id: string;
  tag_ids: string[];
  created_at: string;
}

export interface TagFilter {
  id: string;
  name: string;
  color: string;
  category: string;
  is_active: boolean;
}

class TagService {
  private static instance: TagService;
  private cache: Map<string, any> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): TagService {
    if (!TagService.instance) {
      TagService.instance = new TagService();
    }
    return TagService.instance;
  }

  // Cache management
  private getCached(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  private setCached(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  private clearCache(): void {
    this.cache.clear();
  }

  // Load user tags
  async getUserTags(userId: string): Promise<UserTag[]> {
    const cacheKey = `user_tags_${userId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('user_tags')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      this.setCached(cacheKey, data || []);
      return data || [];
    } catch (error) {
      console.error('Error loading user tags:', error);
      throw error;
    }
  }

  // Load system tags (keeping for backwards compatibility, but returns empty)
  async getSystemTags(): Promise<SystemTag[]> {
    // System tags table doesn't exist, return empty array
    return [];
  }

  // Load all tags (user only - no system tags)
  async getAllTags(userId: string): Promise<UserTag[]> {
    try {
      const userTags = await this.getUserTags(userId);
      return userTags.map(tag => ({ ...tag, is_system: false }));
    } catch (error) {
      console.error('Error loading all tags:', error);
      throw error;
    }
  }

  // Create new tag
  async createTag(userId: string, tagData: Omit<UserTag, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<UserTag> {
    try {
      const { data, error } = await supabase
        .from('user_tags')
        .insert({
          user_id: userId,
          name: tagData.name.trim(),
          description: tagData.description?.trim() || null,
          color: tagData.color,
          category: tagData.category
        })
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearCache();

      return data;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw error;
    }
  }

  // Update tag
  async updateTag(userId: string, tagId: string, updates: Partial<Omit<UserTag, 'id' | 'user_id' | 'created_at'>>): Promise<UserTag> {
    try {
      const { data, error } = await supabase
        .from('user_tags')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', tagId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearCache();

      return data;
    } catch (error) {
      console.error('Error updating tag:', error);
      throw error;
    }
  }

  // Delete tag
  async deleteTag(userId: string, tagId: string): Promise<boolean> {
    try {
      // Check if tag is in use
      const isInUse = await this.isTagInUse(userId, tagId);
      if (isInUse) {
        throw new Error('Cannot delete tag that is currently in use');
      }

      const { error } = await supabase
        .from('user_tags')
        .delete()
        .eq('id', tagId)
        .eq('user_id', userId);

      if (error) throw error;

      // Clear cache
      this.clearCache();

      return true;
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw error;
    }
  }

  // Check if tag is in use
  async isTagInUse(userId: string, tagId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('tagged_data')
        .select('id')
        .eq('user_id', userId)
        .contains('tag_ids', [tagId])
        .limit(1);

      if (error) throw error;

      return (data || []).length > 0;
    } catch (error) {
      console.error('Error checking tag usage:', error);
      return false;
    }
  }

  // Tag data with tags
  async tagData(userId: string, dataType: string, dataId: string, tagIds: string[]): Promise<TaggedData> {
    try {
      // Check if data is already tagged
      const { data: existingData, error: checkError } = await supabase
        .from('tagged_data')
        .select('*')
        .eq('user_id', userId)
        .eq('data_type', dataType)
        .eq('data_id', dataId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
      }

      if (existingData) {
        // Update existing tagged data
        const { data, error } = await supabase
          .from('tagged_data')
          .update({
            tag_ids: tagIds,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new tagged data
        const { data, error } = await supabase
          .from('tagged_data')
          .insert({
            user_id: userId,
            data_type: dataType,
            data_id: dataId,
            tag_ids: tagIds
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error tagging data:', error);
      throw error;
    }
  }

  // Remove tags from data
  async untagData(userId: string, dataType: string, dataId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tagged_data')
        .delete()
        .eq('user_id', userId)
        .eq('data_type', dataType)
        .eq('data_id', dataId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error untagging data:', error);
      throw error;
    }
  }

  // Get tagged data for a user
  async getTaggedData(userId: string, dataType?: string): Promise<TaggedData[]> {
    try {
      let query = supabase
        .from('tagged_data')
        .select('*')
        .eq('user_id', userId);

      if (dataType) {
        query = query.eq('data_type', dataType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error loading tagged data:', error);
      throw error;
    }
  }

  // Filter data by tags
  async filterDataByTags<T>(
    userId: string,
    dataType: string,
    data: T[],
    selectedTags: string[],
    dataIdField: keyof T = 'id' as keyof T
  ): Promise<T[]> {
    if (selectedTags.length === 0) {
      return data;
    }

    try {
      // Get tagged data for the selected tags
      const { data: taggedData, error } = await supabase
        .from('tagged_data')
        .select('data_id')
        .eq('user_id', userId)
        .eq('data_type', dataType)
        .overlaps('tag_ids', selectedTags);

      if (error) throw error;

      const taggedDataIds = new Set(taggedData?.map(td => td.data_id) || []);

      // Filter the original data to only include tagged items
      return data.filter(item => taggedDataIds.has(String(item[dataIdField])));
    } catch (error) {
      console.error('Error filtering data by tags:', error);
      return data; // Return original data if filtering fails
    }
  }

  // Get tag usage statistics
  async getTagUsageStats(userId: string): Promise<{ tagId: string; usageCount: number }[]> {
    try {
      const { data, error } = await supabase
        .from('tagged_data')
        .select('tag_ids')
        .eq('user_id', userId);

      if (error) throw error;

      const tagCounts: { [key: string]: number } = {};
      
      data?.forEach(item => {
        item.tag_ids?.forEach(tagId => {
          tagCounts[tagId] = (tagCounts[tagId] || 0) + 1;
        });
      });

      return Object.entries(tagCounts).map(([tagId, count]) => ({
        tagId,
        usageCount: count
      }));
    } catch (error) {
      console.error('Error getting tag usage stats:', error);
      return [];
    }
  }

  // Get data by specific tag
  async getDataByTag(userId: string, tagId: string, dataType?: string): Promise<TaggedData[]> {
    try {
      let query = supabase
        .from('tagged_data')
        .select('*')
        .eq('user_id', userId)
        .contains('tag_ids', [tagId]);

      if (dataType) {
        query = query.eq('data_type', dataType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting data by tag:', error);
      throw error;
    }
  }

  // Bulk tag operations
  async bulkTagData(userId: string, operations: Array<{
    dataType: string;
    dataId: string;
    tagIds: string[];
  }>): Promise<TaggedData[]> {
    try {
      const results: TaggedData[] = [];

      for (const operation of operations) {
        try {
          const result = await this.tagData(
            userId,
            operation.dataType,
            operation.dataId,
            operation.tagIds
          );
          results.push(result);
        } catch (error) {
          console.error(`Error tagging data ${operation.dataId}:`, error);
          // Continue with other operations
        }
      }

      return results;
    } catch (error) {
      console.error('Error in bulk tag operations:', error);
      throw error;
    }
  }

  // Search tags
  async searchTags(userId: string, searchTerm: string, category?: string): Promise<(UserTag | SystemTag)[]> {
    try {
      const allTags = await this.getAllTags(userId);
      
      return allTags.filter(tag => {
        const matchesSearch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             tag.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !category || tag.category === category;
        
        return matchesSearch && matchesCategory;
      });
    } catch (error) {
      console.error('Error searching tags:', error);
      return [];
    }
  }

  // Get tag suggestions based on data type
  async getTagSuggestions(userId: string, dataType: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('tagged_data')
        .select('tag_ids')
        .eq('user_id', userId)
        .eq('data_type', dataType);

      if (error) throw error;

      const tagFrequency: { [key: string]: number } = {};
      
      data?.forEach(item => {
        item.tag_ids?.forEach(tagId => {
          tagFrequency[tagId] = (tagFrequency[tagId] || 0) + 1;
        });
      });

      // Return top 5 most used tags for this data type
      return Object.entries(tagFrequency)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([tagId]) => tagId);
    } catch (error) {
      console.error('Error getting tag suggestions:', error);
      return [];
    }
  }
}

export const tagService = TagService.getInstance();
